/* ==========================================================================
   Product options app (VO Product Options) — presentation layer.

   The app renders frame choices as bare swatch images: no heading saying which
   frame is selected, no name or surcharge under each swatch, and no affordance
   that the row scrolls. product-options.css hides the app's own labels and
   tooltips on the assumption that this script supplies replacements, so the two
   files ship together — the CSS alone would leave the options unlabelled.

   Ported from the temp-ecs storefront, where both halves lived inline in
   theme.liquid. Differences: appearance lives in the stylesheet instead of
   inline cssText so the widget follows the section's colour scheme, the two
   near-identical arrow builders are one helper, and the selected frame name is
   set as text rather than interpolated into innerHTML.

   The polling and MutationObserver are not optional. The app injects its markup
   asynchronously well after DOMContentLoaded, and rebuilds it wholesale
   whenever the shopper changes size, discarding anything added to it.
   ========================================================================== */
(function () {
  'use strict';

  var POLL_MS = 200;
  var MAX_ATTEMPTS = 40; /* ~8s, then give up: the app is absent or broken. */
  var SCROLL_STEP = 200;
  var SCROLL_EPSILON = 5; /* Sub-pixel layout means scrollWidth is never exact. */

  /* Capture before this bridge can write its selected variant into the URL. */
  var ENTRY_PARAMS = new URLSearchParams(window.location.search);
  var SITEWIDE_DEFAULT_ENABLED = true; /* ECAS sitewide default, 2026-10-22. */
  var ROUTE66_PDP_ENABLED = true; /* Route 66 PDP, 2026-10-23. */
  var route66Pdp = null;
  var legacyFramePdp = null;
  var ROUTE66_PDP_CSS = ".ecas-route66-pdp #bcpo .bcpo-buttons {\n  display: block !important;\n}\n.ecas-route66-pdp #bcpo .bcpo-label,\n.ecas-route66-pdp #bcpo .ecas-arr,\n.ecas-route66-pdp #bcpo #ecas-header-container,\n.ecas-route66-pdp #bcpo .ecas-frame-name,\n.ecas-route66-pdp #bcpo .ecas-frame-price {\n  display: none !important;\n}\n.ecas-route66-pdp #bcpo .ecas-size-wrap,\n.ecas-route66-pdp #bcpo .ecas-wrap {\n  overflow: visible !important;\n}\n.ecas-route66-pdp #bcpo .ecas-cro-heading {\n  color: #192d24 !important;\n  font-size: 17px !important;\n  line-height: 1.35 !important;\n  font-weight: 650 !important;\n  letter-spacing: normal !important;\n  margin: 0 0 10px !important;\n  padding: 0 !important;\n  text-transform: none !important;\n}\n.ecas-route66-pdp #bcpo .ecas-cro-size-grid {\n  display: grid !important;\n  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;\n  gap: 10px !important;\n  max-height: none !important;\n  height: auto !important;\n  overflow: visible !important;\n  white-space: normal !important;\n  padding: 0 !important;\n  margin: 0 !important;\n}\n.ecas-route66-pdp #bcpo .ecas-cro-size-grid > div {\n  display: block !important;\n  min-width: 0 !important;\n  width: auto !important;\n  margin: 0 !important;\n  line-height: 1.35 !important;\n}\n.ecas-route66-pdp #bcpo input.bcpo-front-button-option,\n.ecas-route66-pdp #bcpo input.bcpo-front-image-option {\n  display: block !important;\n  position: absolute !important;\n  width: 1px !important;\n  height: 1px !important;\n  opacity: 0 !important;\n  overflow: hidden !important;\n  clip-path: inset(50%) !important;\n  margin: 0 !important;\n  padding: 0 !important;\n}\n.ecas-route66-pdp #bcpo .ecas-cro-size-grid label.bcpo-front-button-label {\n  position: relative !important;\n  display: flex !important;\n  flex-direction: column !important;\n  align-items: center !important;\n  justify-content: center !important;\n  min-height: 70px !important;\n  width: 100% !important;\n  margin: 0 !important;\n  padding: 11px 8px !important;\n  border: 1px solid #748278 !important;\n  border-radius: 6px !important;\n  background: #fff !important;\n  color: #192d24 !important;\n  white-space: normal !important;\n  line-height: 1.35 !important;\n  text-align: center !important;\n  box-sizing: border-box !important;\n}\n.ecas-route66-pdp .ecas-cro-inches { font-size: 16px !important; font-weight: 650 !important; }\n.ecas-route66-pdp .ecas-cro-metric { font-size: 13px !important; color: #526157 !important; margin-top: 3px !important; }\n.ecas-route66-pdp #bcpo .ecas-cro-size-grid input:checked + label.bcpo-front-button-label {\n  background: #203e30 !important;\n  color: #fff !important;\n  border: 2px solid #203e30 !important;\n  padding: 10px 7px !important;\n}\n.ecas-route66-pdp #bcpo .ecas-cro-size-grid input:checked + label .ecas-cro-metric { color: #fff !important; }\n.ecas-route66-pdp #bcpo .ecas-cro-size-grid input:checked + label::after {\n  content: '\u2713'; position: absolute; top: 4px; right: 6px; font-size: 13px; line-height: 1;\n}\n.ecas-route66-pdp #bcpo input:focus-visible + label,\n.ecas-route66-pdp .ecas-cro-format button:focus-visible {\n  outline: 3px solid #141d18 !important;\n  outline-offset: 3px !important;\n}\n.ecas-route66-pdp #bcpo > .ecas-cro-format { margin: 22px 0 0 !important; padding: 0 !important; }\n.ecas-route66-pdp .ecas-cro-format-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }\n.ecas-route66-pdp .ecas-cro-format button {\n  min-height: 76px; display: flex; flex-direction: column; align-items: flex-start; justify-content: center;\n  gap: 4px; padding: 12px 15px; border: 1px solid #748278; border-radius: 6px; background: #fff;\n  color: #192d24; font: inherit; line-height: 1.35; text-align: left; cursor: pointer; position: relative;\n}\n.ecas-route66-pdp .ecas-cro-format button span { font-size: 15px; }\n.ecas-route66-pdp .ecas-cro-format button strong { font-size: 19px; font-weight: 650; }\n.ecas-route66-pdp .ecas-cro-format button[aria-pressed='true'] { border: 2px solid #203e30; padding: 11px 14px; background: #eef4ef; }\n.ecas-route66-pdp .ecas-cro-format button[aria-pressed='true']::after { content: '\u2713'; position: absolute; right: 12px; top: 10px; font-size: 17px; }\n.ecas-route66-pdp #bcpo .ecas-cro-finish-row:has(input[type='radio']:not([disabled])) {\n  overflow: visible !important; white-space: normal !important; margin-top: 20px !important; font-size: 16px !important;\n}\n.ecas-route66-pdp #bcpo .ecas-cro-finish-row:not(:has(input[type='radio']:not([disabled]))) { display: none !important; }\n.ecas-route66-pdp #bcpo .ecas-cro-finish-row:has(input[type='radio']:not([disabled])) > .single-option-selector {\n  display: grid !important; grid-template-columns: repeat(3, minmax(0, 1fr)) !important; gap: 10px !important;\n  white-space: normal !important; margin: 0 !important; padding: 0 !important; position: relative !important;\n}\n.ecas-route66-pdp #bcpo .ecas-cro-finish-row div.bcpo-image-parent {\n  width: auto !important; min-width: 0 !important; max-width: none !important; display: block !important;\n  margin: 0 !important; padding: 0 !important; line-height: 1.3 !important;\n}\n.ecas-route66-pdp #bcpo .ecas-cro-finish-row label.bcpo-front-image-label {\n  position: relative !important; display: block !important; width: 100% !important; height: auto !important;\n  min-height: 122px !important; margin: 0 !important; padding: 86px 6px 10px !important;\n  border: 1px solid #748278 !important; border-radius: 6px !important;\n  background-size: 70px 70px !important; background-position: center 9px !important;\n  background-repeat: no-repeat !important; background-color: #fff !important;\n  color: #192d24 !important; text-align: center !important; box-sizing: border-box !important;\n}\n.ecas-route66-pdp #bcpo .ecas-cro-finish-row input:checked + label.bcpo-front-image-label {\n  border: 3px solid #203e30 !important; padding: 84px 4px 8px !important; background-position: center 7px !important;\n}\n.ecas-route66-pdp #bcpo .ecas-cro-finish-row input:checked + label.bcpo-front-image-label::after {\n  content: '\u2713'; position: absolute; top: 5px; right: 5px; display: grid; place-items: center;\n  width: 21px; height: 21px; border-radius: 50%; background: #203e30; color: #fff; font-size: 14px; line-height: 1;\n}\n.ecas-route66-pdp .ecas-cro-finish-name { display: block !important; font-size: 13px !important; font-weight: 600 !important; line-height: 1.3 !important; }\n/* A selected extra stays visible until an actual VO radio selection replaces it. */\n.ecas-route66-pdp #bcpo .ecas-cro-finish-row .ecas-cro-extra:not(:has(input:checked)) { display: none !important; }\n/* The real zero-price radio stays measurable/submittable behind its format button. */\n.ecas-route66-pdp #bcpo .ecas-cro-finish-row .ecas-cro-print-option {\n  position: absolute !important; left: 0 !important; top: 0 !important; width: 1px !important; min-width: 1px !important;\n  max-width: 1px !important; height: 1px !important; overflow: hidden !important; clip-path: inset(50%) !important;\n  margin: 0 !important; padding: 0 !important;\n}\n/* Hide finishes only while the actual zero-price radio is checked. */\n.ecas-route66-pdp #bcpo .ecas-cro-finish-row:has(.ecas-cro-print-option input:checked) { display: none !important; }\n.ecas-route66-pdp [data-price],\n.ecas-route66-pdp-page [data-sticky-price] { position: relative; }\n.ecas-route66-pdp [data-price] .price { gap: 9px 12px; align-items: baseline; flex-wrap: wrap; }\n.ecas-route66-pdp [data-price] .price__sale { font-size: 36px; line-height: 1.15; font-weight: 700; color: #192d24; }\n.ecas-route66-pdp [data-price] .price__compare { font-size: 18px; color: #58645c; }\n.ecas-route66-pdp [data-price] .price__badge,\n.ecas-route66-pdp-page [data-sticky-price] .price__badge {\n  background: #e8f2e9; color: #244b30; border: 1px solid #b7cbb9; border-radius: 4px; font-size: 14px; font-weight: 650; padding: 4px 7px;\n}\n.ecas-route66-pdp .ecas-cro-summary { font-size: 15px; line-height: 1.5; color: #334a3b; margin: 10px 0 20px; }\n.ecas-route66-pdp .ecas-cro-currency,\n.ecas-route66-pdp-page [data-sticky-price] .ecas-cro-currency { display: inline-block; font-size: 12px; color: #526157; margin-left: 8px; }\n.ecas-route66-pdp .ecas-cro-status { color: #334a3b; font-size: 14px; line-height: 1.4; margin: 10px 0 0; }\n.ecas-route66-pdp .ecas-cro-status:empty { display: none; }\n.ecas-route66-pdp-page .ecas-price-pending .price { visibility: hidden; }\n.ecas-route66-pdp-page .ecas-price-pending::after { content: 'Updating\u2026'; position: absolute; top: 0; left: 0; font-size: 22px; font-weight: 600; color: #334a3b; }\n.ecas-route66-pdp-page [data-sticky-price].ecas-price-pending::after { font-size: 16px; }\n.ecas-route66-pdp-page [data-sticky-variant] { font-size: 13px !important; color: #334a3b; line-height: 1.35; }\n@media (max-width: 749px) {\n  .ecas-route66-pdp #bcpo .ecas-cro-size-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }\n  .ecas-route66-pdp [data-price] .price__sale { font-size: 34px; }\n}\n\n/* Main and card prices already include the chosen frame. */\n.ecas-route66-pdp #bcpo .bcpo-price-addons { display: none !important; }\n.ecas-route66-pdp .ecas-cro-size-price { display: block !important; font-size: 16px !important; font-weight: 700 !important; margin-top: 7px !important; line-height: 1.35 !important; }\n\n/* Route 66 mobile spacing, 2026-10-23. The two original gallery images remain intact. */\n@media (max-width: 749px) {\n  .ecas-route66-pdp .product-page {\n    row-gap: 22px;\n    padding-top: 16px;\n  }\n\n  .ecas-route66-pdp .product-gallery {\n    display: flex;\n    flex-direction: column;\n    gap: 10px;\n    min-width: 0;\n  }\n\n  .ecas-route66-pdp .product-gallery__main {\n    order: 0;\n    width: 100%;\n    min-width: 0;\n  }\n\n  .ecas-route66-pdp .product-gallery__thumbs {\n    order: 1;\n    display: flex;\n    flex-wrap: wrap;\n    gap: 8px;\n    justify-content: flex-start;\n    overflow: visible;\n    padding: 3px 0;\n  }\n\n  .ecas-route66-pdp .product-gallery__thumb {\n    flex: 0 0 56px;\n    width: 56px;\n    height: 56px;\n  }\n\n  .ecas-route66-pdp .product-gallery__thumb img {\n    display: block;\n    width: 100%;\n    height: 100%;\n  }\n\n  .ecas-route66-pdp .product-info {\n    gap: 12px;\n  }\n\n  .ecas-route66-pdp .product-info__rating {\n    margin: 0;\n    font-size: 15px;\n    line-height: 1.45;\n  }\n\n  .ecas-route66-pdp .product-info > h1 {\n    margin: 0;\n    line-height: 1.15;\n  }\n\n  .ecas-route66-pdp [data-price] {\n    display: flex;\n    flex-wrap: wrap;\n    align-items: baseline;\n    gap: 4px 8px;\n  }\n\n  .ecas-route66-pdp [data-price] .price {\n    flex: 0 1 auto;\n    max-width: 100%;\n    gap: 6px 10px;\n  }\n\n  .ecas-route66-pdp [data-price] .ecas-cro-currency {\n    margin: 0;\n    line-height: 1.2;\n    white-space: nowrap;\n  }\n\n  .ecas-route66-pdp [data-price].ecas-price-pending .ecas-cro-currency {\n    visibility: hidden;\n  }\n\n  .ecas-route66-pdp .ecas-cro-summary {\n    margin: 0;\n    line-height: 1.5;\n  }\n}\n";
  var MAX_DEFAULT_ATTEMPTS = 150; /* ~30s for a slow app; user gestures cancel. */

  /* Waits for the app to render, then hands the element over once. */
  function whenReady(getEl, done) {
    var attempts = 0;
    var timer = setInterval(function () {
      attempts++;
      var el = getEl();
      if (el) {
        clearInterval(timer);
        done(el);
      } else if (attempts >= MAX_ATTEMPTS) {
        clearInterval(timer);
      }
    }, POLL_MS);
  }

  /* ------------------------------------------------------------------
     Horizontal scroll affordance, shared by the swatch and size rows
     ------------------------------------------------------------------ */
  function buildArrow(direction, row) {
    var accessible = !!legacyFramePdp;
    var arrow = document.createElement(accessible ? 'button' : 'div');
    if (accessible) {
      arrow.type = 'button';
      arrow.style.border = '0';
      arrow.style.padding = '0';
      arrow.setAttribute('aria-label', direction === 'left' ? 'Show previous options' : 'Show more options');
    }
    arrow.className = 'ecas-arr ecas-arr--' + direction;
    arrow.innerHTML = direction === 'left' ? '&#8249;' : '&#8250;';
    arrow.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      /* The app binds its own click handler on the swatch container, which
         would read an arrow click as picking whichever swatch sits beneath. */
      event.stopImmediatePropagation();
      var max = row.scrollWidth - row.clientWidth;
      var next = direction === 'left'
        ? Math.max(0, row.scrollLeft - SCROLL_STEP)
        : Math.min(max, row.scrollLeft + SCROLL_STEP);
      row.scrollTo({ left: next, behavior: 'smooth' });
    });
    arrow.addEventListener('mousedown', function (event) {
      event.stopPropagation();
    });
    return arrow;
  }

  /* Wraps a scrolling row and returns its update fn, or null if it already
     fits or is already wrapped. */
  function addArrows(row, wrapClass) {
    var existing = row.parentNode;
    if (existing && existing.classList && existing.classList.contains(wrapClass)) {
      return existing._ecasUpdate || null;
    }
    if (row.scrollWidth <= row.clientWidth + SCROLL_EPSILON) return null;

    var wrap = document.createElement('div');
    wrap.className = wrapClass;
    row.parentNode.insertBefore(wrap, row);
    wrap.appendChild(row);
    row.style.maxWidth = '100%';
    row.style.width = '100%';

    var left = buildArrow('left', row);
    var right = buildArrow('right', row);
    wrap.appendChild(left);
    wrap.appendChild(right);

    var update = function () {
      var max = row.scrollWidth - row.clientWidth;
      if (max <= SCROLL_EPSILON) {
        left.classList.remove('is-visible');
        right.classList.remove('is-visible');
        return;
      }
      left.classList.toggle('is-visible', row.scrollLeft > 3);
      right.classList.toggle('is-visible', row.scrollLeft < max - 3);
    };

    wrap._ecasUpdate = update;
    row.addEventListener('scroll', update);
    update();

    if (window.ResizeObserver) {
      new ResizeObserver(update).observe(row);
    }
    return update;
  }

  /* ------------------------------------------------------------------
     Price bridge

     The app owns the size row and the priced extras, and it hides the theme's
     own variant picker — which is the control theme.js listens to. Left alone
     the headline price stays on whatever variant Liquid rendered: it does not
     follow the size the shopper picks, and the surcharge printed under each
     frame swatch never reaches it.

     The app's markup is the only record of the choice, so read it back after
     every interaction and hand it to theme.js, which matches the values to a
     variant and repaints both the buy box and the sticky bar.
     ------------------------------------------------------------------ */
  var APP_ROOTS = '#bcpo, [id^="bcpo-select-option-"]';
  var OPTION_WIDGET_PREFIX = 'bcpo-select-option-';
  /* The app repaints after the click that caused the change, so a read taken
     as the event fires still sees the old markup. Read again once it settles;
     theme.js ignores a repeat of the state it has already rendered. */
  var SYNC_DELAYS = [0, 200, 500];

  /* The app numbers its option widgets by option index, so
     #bcpo-select-option-0 is the product's first option. Each is a <select> or
     a row of buttons, depending on how the merchant configured the option. */
  function appOptionValues() {
    var values = [];
    document.querySelectorAll('[id^="' + OPTION_WIDGET_PREFIX + '"]').forEach(function (widget) {
      var index = parseInt(widget.id.slice(OPTION_WIDGET_PREFIX.length), 10);
      if (isNaN(index)) return;
      var value = widgetValue(widget);
      if (value) values[index] = value;
    });
    return values;
  }

  function widgetValue(widget) {
    if (widget.tagName === 'SELECT') return widget.value;
    var checked = widget.querySelector('input:checked');
    if (checked) return checked.value;
    var select = widget.querySelector('select');
    if (select) return select.value;
    /* A button row can mark the pick with a class rather than an input. */
    var active = widget.querySelector('.bcpo-selected, [aria-checked="true"], [aria-selected="true"]');
    if (active) return (active.getAttribute('data-value') || active.textContent || '').trim();
    return '';
  }

  /* What the shopper's current extras add, in cents. data-price is in currency
     units — the same attribute render() prints as "+$…" under each swatch.

     Only inputs that are enabled and actually on screen count. The app emits
     each option twice, once as the dropdown the stylesheet hides and once as
     the visible row, and it leaves the frame rows belonging to the other sizes
     in the DOM with their inputs disabled. */
  function surchargeCents() {
    var scopes = ['#bcpo', '[id^="' + OPTION_WIDGET_PREFIX + '"]'];
    var types = ['input[type="radio"][data-price]', 'input[type="checkbox"][data-price]'];
    var selector = [];
    scopes.forEach(function (scope) {
      types.forEach(function (type) {
        selector.push(scope + ' ' + type);
      });
    });

    var total = 0;
    /* querySelectorAll returns each element once, so an input matched by both
       scopes — the size widget sits inside #bcpo — is not counted twice. */
    document.querySelectorAll(selector.join(', ')).forEach(function (input) {
      if (!input.checked || input.disabled || !isOnScreen(input)) return;
      var amount = parseFloat(input.getAttribute('data-price'));
      if (isFinite(amount) && amount > 0) total += amount;
    });
    return Math.round(total * 100);
  }

  /* A swatch input is invisible by design — the label beside it is the swatch —
     so whether it is on screen has to be judged from the cell holding it. */
  function isOnScreen(el) {
    return !!(el.parentElement && el.parentElement.offsetParent);
  }

  function syncPrice() {
    var api = window.themeProduct;
    if (!api || typeof api.setAppSelection !== 'function') {
      if (legacyFramePdp) legacyFramePdp.invalidate();
      return;
    }
    var controller = route66Pdp && route66Pdp.active ? route66Pdp : null;
    if (controller && !controller.beforeSync()) {
      if (controller.active) controller.afterSync();
      return;
    }
    api.setAppSelection({ options: appOptionValues(), surcharge: surchargeCents() });
    if (controller && controller.active) controller.afterSync();
    else if (legacyFramePdp) legacyFramePdp.afterSync();
  }

  function scheduleSync() {
    SYNC_DELAYS.forEach(function (delay) {
      setTimeout(syncPrice, delay);
    });
  }


  /* Route 66 only. The original VO radios remain the submitted controls. */
  function initRoute66Pdp() {
    if (!ROUTE66_PDP_ENABLED && ENTRY_PARAMS.get('ecas_pdp_preview') !== '1') return null;
    var wrapper = document.querySelector('[data-product-form-wrapper][data-update-url="true"]');
    var dataEl = wrapper && wrapper.querySelector('[data-product-json]');
    var form = wrapper && wrapper.querySelector('form[data-product-form]');
    if (!dataEl || !form) return null;
    var product;
    try { product = JSON.parse(dataEl.textContent); } catch (err) { return null; }
    if (product.handle !== 'route-66-poster-vintage-california-usa' || !Array.isArray(product.variants)) return null;

    function normalise(value) {
      return String(value == null ? '' : value).toLowerCase().replace(/\s+/g, '').replace(/×/g, 'x');
    }
    function sizeKey(value) {
      var match = normalise(value).match(/^(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)/);
      return match ? match[1] + 'x' + match[2] : '';
    }
    function dimension(value) {
      return String(value || '').trim().replace(/(\d)\s*[x×]\s*(\d)/g, '$1 × $2')
        .replace(/(\d)\s*(in|cm)\b/gi, '$1 $2');
    }
    function text(el, value) { if (el && el.textContent !== value) el.textContent = value; }
    function attr(el, name, value) { if (el && el.getAttribute(name) !== String(value)) el.setAttribute(name, String(value)); }
    function allowed(value) { return /^(naturalwood|blackwood|whitewood)$/.test(normalise(value)); }
    function printOnly(value) { return /noframe|printonly/.test(normalise(value)); }
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || product.currency || 'USD';
    function money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: currency, minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2
      }).format(cents / 100);
    }
    var sizeIndex = (product.options || []).findIndex(function (option) {
      return normalise(typeof option === 'string' ? option : option.name) === 'size';
    });
    if (sizeIndex < 0) return null;
    var idInput = form.querySelector('input[name="id"]');
    var initialVariant = product.variants.find(function (variant) { return String(variant.id) === String(idInput && idInput.value); });
    var defaultVariant = !ENTRY_PARAMS.has('variant') && !ENTRY_PARAMS.has('option_values') && initialVariant
      ? product.variants.find(function (variant) {
        return variant.available && variant.options.every(function (value, index) {
          return index === sizeIndex ? sizeKey(value) === '8x10' : normalise(value) === normalise(initialVariant.options[index]);
        });
      }) : null;

    var state = {
      active: true, root: null, pending: true, choosing: false, refreshing: false,
      defaultVariant: defaultVariant, desired: defaultVariant ? 'Natural Wood' : null,
      lastFinish: 'Natural Wood', variant: null, row: null, generation: 0,
      lastAction: 0, actionKey: '', actionCount: 0, pendingSince: Date.now(), lastSize: '', expectedSize: null,
      initialConditionRefreshSent: false
    };
    var poll = null;
    var api = { active: true, beforeSync: refresh, afterSync: afterSync, refresh: refresh };
    var mainPrice = wrapper.querySelector('[data-price]');
    var stickyPrice = document.querySelector('[data-sticky-price]');
    var stickyVariant = document.querySelector('[data-sticky-variant]');
    var addButton = form.querySelector('[data-add-to-cart]');
    var stickyButton = document.querySelector('[data-sticky-add]');
    var summary = document.createElement('p');
    summary.className = 'ecas-cro-summary';
    if (mainPrice) mainPrice.insertAdjacentElement('afterend', summary);
    var live = document.createElement('p');
    live.className = 'visually-hidden ecas-cro-live';
    live.setAttribute('aria-live', 'polite');
    wrapper.appendChild(live);
    var status = document.createElement('p');
    status.className = 'ecas-cro-status';
    status.setAttribute('role', 'status');
    var format = document.createElement('div');
    format.className = 'ecas-cro-format';
    format.setAttribute('role', 'group');
    format.setAttribute('aria-label', 'Choose print only or framed');
    format.innerHTML = '<p class="ecas-cro-heading">Format</p><div class="ecas-cro-format-grid">' +
      '<button type="button" data-ecas-format="print" aria-pressed="false"><span>Print only</span><strong></strong></button>' +
      '<button type="button" data-ecas-format="framed" aria-pressed="false"><span>Framed</span><strong></strong></button></div>';
    format.appendChild(status);
    var printButton = format.querySelector('[data-ecas-format="print"]');
    var framedButton = format.querySelector('[data-ecas-format="framed"]');
    wrapper.classList.add('ecas-route66-pdp');
    document.body.classList.add('ecas-route66-pdp-page');
    var style = document.createElement('style');
    style.id = 'ecas-route66-pdp-styles';
    style.textContent = ROUTE66_PDP_CSS;
    document.head.appendChild(style);

    function widget() { return document.getElementById(OPTION_WIDGET_PREFIX + sizeIndex); }
    function currentVariant() {
      var control = widget();
      if (!control) return null;
      var options = appOptionValues();
      if (!options[sizeIndex]) return null;
      return product.variants.find(function (variant) {
        return variant.options.every(function (value, index) {
          return normalise(value) === normalise(options[index] == null && initialVariant ? initialVariant.options[index] : options[index]);
        });
      }) || null;
    }
    function inputs(row) { return row ? Array.prototype.slice.call(row.querySelectorAll('input.bcpo-front-image-option[type="radio"]')) : []; }
    function selected(row) { return inputs(row).find(function (input) { return !input.disabled && input.checked; }); }
    function rows() { return state.root ? Array.prototype.slice.call(state.root.querySelectorAll('.bcpo-images')) : []; }
    function activeRow(variant) {
      if (!variant) return null;
      var enabled = rows().filter(function (row) { return inputs(row).some(function (input) { return !input.disabled; }); });
      if (enabled.length !== 1) return null;
      var expected = 'properties[frameoptions-' + sizeKey(variant.options[sizeIndex]) + ']';
      return inputs(enabled[0]).some(function (input) { return !input.disabled && normalise(input.name) === expected; }) ? enabled[0] : null;
    }
    function inputFor(row, value) {
      return inputs(row).find(function (input) {
        return !input.disabled && (printOnly(value) ? printOnly(input.value) : normalise(input.value) === normalise(value));
      });
    }
    function labelFor(input) {
      return input && input.parentElement && Array.prototype.find.call(input.parentElement.querySelectorAll('label'), function (label) {
        return label.htmlFor === input.id;
      });
    }
    function choose(input) {
      if (!input || input.disabled) return false;
      var label = labelFor(input);
      if (!label) return false;
      var scrollX = window.scrollX;
      var scrollY = window.scrollY;
      var focused = document.activeElement;
      state.choosing = true;
      state.lastAction = Date.now();
      try {
        label.click();
        if (focused && focused !== document.body && focused.isConnected && focused.focus) {
          focused.focus({ preventScroll: true });
        } else if (document.activeElement && document.activeElement !== focused && document.activeElement.blur) {
          document.activeElement.blur();
        }
        /* Always cancel a focus scroll, even if CSS smooth scrolling has not moved yet. */
        window.scrollTo({ left: scrollX, top: scrollY, behavior: 'instant' });
      } finally { state.choosing = false; }
      scheduleSync();
      return true;
    }
    function request(value) {
      state.defaultVariant = null;
      state.desired = value;
      if (allowed(value)) state.lastFinish = value;
      state.generation++;
      state.actionKey = '';
      state.actionCount = 0;
      state.lastAction = Date.now();
      state.pendingSince = Date.now();
      state.pending = true;
      guard();
      scheduleSync();
    }
    function guard() {
      if (!state.active) return;
      if (state.pending) {
        if (!state.pendingSince) state.pendingSince = Date.now();
        if (!poll) poll = setInterval(syncPrice, POLL_MS);
      } else {
        state.pendingSince = 0;
        clearInterval(poll);
        poll = null;
      }
      [addButton, stickyButton].forEach(function (button) {
        if (!button) return;
        if (state.pending) {
          button.setAttribute('data-ecas-pending', 'true');
          button.disabled = true;
        } else if (button.hasAttribute('data-ecas-pending')) {
          button.removeAttribute('data-ecas-pending');
          button.disabled = !!(state.variant && !state.variant.available);
        }
      });
      [mainPrice, stickyPrice].forEach(function (price) {
        if (!price) return;
        price.classList.toggle('ecas-price-pending', state.pending);
        attr(price, 'aria-busy', state.pending);
      });
      attr(wrapper, 'data-ecas-configuration-state', state.pending ? 'pending' : 'ready');
      text(status, state.pending ? 'Updating your selection…' : '');
    }
    function cancelDefault() {
      if (!state.defaultVariant) return;
      state.defaultVariant = null;
      var actual = selected(activeRow(currentVariant()));
      state.desired = actual && allowed(actual.value) ? actual.value : 'No Frame/ Print Only';
      if (actual && allowed(actual.value)) state.lastFinish = actual.value;
      state.actionKey = '';
      state.actionCount = 0;
      state.generation++;
    }
    function sourceInput(target) {
      if (!target || !target.closest) return null;
      if (target.matches('input.bcpo-front-button-option, input.bcpo-front-image-option')) return target;
      var label = target.closest('label[for]');
      var input = label && document.getElementById(label.htmlFor);
      return input && input.matches('input.bcpo-front-button-option, input.bcpo-front-image-option') ? input : null;
    }
    function onGesture(event) {
      if (!state.active || state.choosing || !event.isTrusted || !event.target || !event.target.closest) return;
      if (!event.target.closest('#bcpo, [data-add-to-cart], [data-sticky-add]')) return;
      cancelDefault();
      var input = sourceInput(event.target);
      if (input && (event.type === 'click' || event.type === 'change' || event.type === 'input')) {
        if (input.classList.contains('bcpo-front-image-option')) {
          request(printOnly(input.value) ? input.value : allowed(input.value) ? input.value : 'Natural Wood');
        } else {
          state.expectedSize = input.value;
          state.lastAction = Date.now();
          state.pendingSince = Date.now();
          state.generation++;
          state.actionKey = '';
          state.actionCount = 0;
          state.pending = true;
          guard();
          scheduleSync();
        }
      }
    }
    function blockPending(event) {
      if (!state.active || !state.pending) return;
      var purchase = event.type === 'submit' ? event.target === form : event.target.closest && event.target.closest('[data-add-to-cart], [data-sticky-add]');
      if (!purchase) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      text(status, 'Please wait while your size and frame update.');
    }
    printButton.addEventListener('click', function () { request('No Frame/ Print Only'); refresh(); });
    framedButton.addEventListener('click', function () { request(state.lastFinish); refresh(); });
    ['pointerdown', 'keydown', 'click', 'change', 'input'].forEach(function (type) { document.addEventListener(type, onGesture, true); });
    document.addEventListener('submit', blockPending, true);
    document.addEventListener('click', blockPending, true);

    function decorateSizes() {
      var control = widget();
      if (!control) return;
      control.classList.add('ecas-cro-size-grid');
      var block = control.closest('.selector-wrapper') || control.parentElement;
      var heading = block.querySelector('.ecas-cro-size-heading');
      if (!heading) {
        heading = document.createElement('p');
        heading.className = 'ecas-cro-heading ecas-cro-size-heading';
        heading.id = 'ecas-route66-size-heading';
        block.insertBefore(heading, block.firstChild);
      }
      text(heading, 'Size');
      attr(control, 'role', 'radiogroup');
      attr(control, 'aria-labelledby', heading.id);
      control.querySelectorAll('input.bcpo-front-button-option').forEach(function (input) {
        var label = labelFor(input);
        if (!label || label.getAttribute('data-ecas-label') === input.value) return;
        var pieces = input.value.split('/');
        label.textContent = '';
        var inches = document.createElement('strong');
        inches.className = 'ecas-cro-inches';
        inches.textContent = dimension(pieces[0]);
        label.appendChild(inches);
        if (pieces[1]) {
          var metric = document.createElement('span');
          metric.className = 'ecas-cro-metric';
          metric.textContent = dimension(pieces[1]);
          label.appendChild(metric);
        }
        label.setAttribute('data-ecas-label', input.value);
      });
      if (format.parentElement !== state.root || format.previousElementSibling !== block) block.insertAdjacentElement('afterend', format);
    }
    function decorateFrames() {
      rows().forEach(function (row) {
        row.classList.add('ecas-cro-finish-row');
        var heading = row.querySelector('.ecas-cro-finish-heading');
        if (!heading) {
          heading = document.createElement('p');
          heading.className = 'ecas-cro-heading ecas-cro-finish-heading';
          heading.textContent = 'Frame finish';
          row.insertBefore(heading, row.firstChild);
        }
        inputs(row).forEach(function (input) {
          var cell = input.closest('.bcpo-image-parent');
          var label = labelFor(input);
          if (!cell || !label) return;
          cell.classList.toggle('ecas-cro-print-option', printOnly(input.value));
          cell.classList.toggle('ecas-cro-extra', !allowed(input.value) && !printOnly(input.value));
          if (printOnly(input.value)) { attr(input, 'tabindex', '-1'); attr(cell, 'aria-hidden', 'true'); }
          if (!label.querySelector('.ecas-cro-finish-name')) {
            var name = document.createElement('span');
            name.className = 'ecas-cro-finish-name';
            name.textContent = input.value;
            label.appendChild(name);
          }
        });
      });
    }
    var observer = new MutationObserver(function () { queue(); });
    var queued = false;
    function queue() {
      if (queued || !state.active) return;
      queued = true;
      setTimeout(function () { queued = false; syncPrice(); }, 0);
    }
    function refresh() {
      if (!state.active) return true;
      if (state.refreshing) return !state.pending;
      if (state.pendingSince && Date.now() - state.pendingSince > 30000) {
        fallback();
        return true;
      }
      state.refreshing = true;
      try {
        var root = document.getElementById('bcpo');
        if (!root) { state.pending = true; guard(); return false; }
        if (root !== state.root) {
          observer.disconnect();
          state.root = root;
          observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'style', 'checked'] });
        }
        decorateSizes();
        decorateFrames();
        var variant = currentVariant();
        if (state.expectedSize && (!variant || normalise(variant.options[sizeIndex]) !== normalise(state.expectedSize))) {
          state.pending = true;
          guard();
          return false;
        }
        if (state.defaultVariant && (!variant || variant.id !== state.defaultVariant.id)) {
          var control = widget();
          var target = control && Array.prototype.find.call(control.querySelectorAll('input.bcpo-front-button-option'), function (input) {
            return !input.disabled && normalise(input.value) === normalise(state.defaultVariant.options[sizeIndex]);
          });
          if (target && Date.now() - state.lastAction >= 500) choose(target);
          state.pending = true;
          guard();
          return false;
        }
        /* Some VO loads select an explicit native variant without running its
           conditions. Bootstrap that same input once; never visit another size. */
        if (!state.initialConditionRefreshSent && !state.lastSize && initialVariant && variant &&
            (ENTRY_PARAMS.has('variant') || ENTRY_PARAMS.has('option_values')) &&
            String(variant.id) === String(initialVariant.id) && Date.now() - state.pendingSince >= 600) {
          var allFrameInputs = root.querySelectorAll('input.bcpo-front-image-option[type="radio"]');
          var currentSizeInput = widget() && widget().querySelector('input.bcpo-front-button-option:checked');
          if (allFrameInputs.length && Array.prototype.every.call(allFrameInputs, function (input) { return input.disabled; }) &&
              currentSizeInput && !currentSizeInput.disabled &&
              normalise(currentSizeInput.value) === normalise(variant.options[sizeIndex])) {
            state.initialConditionRefreshSent = true;
            state.lastAction = Date.now();
            state.choosing = true;
            try { currentSizeInput.dispatchEvent(new Event('change', { bubbles: true })); }
            finally { state.choosing = false; }
            scheduleSync();
          }
        }
        var row = activeRow(variant);
        state.variant = variant;
        state.row = row;
        if (!variant || !row) { state.pending = true; guard(); return false; }
        var key = String(variant.id);
        if (key !== state.lastSize) { state.lastSize = key; state.actionKey = ''; state.actionCount = 0; }
        var actual = selected(row);
        if (state.desired == null) {
          state.desired = actual && allowed(actual.value) ? actual.value : actual && !printOnly(actual.value) ? 'Natural Wood' : 'No Frame/ Print Only';
        }
        var desired = inputFor(row, state.desired);
        if (!desired) {
          desired = inputFor(row, printOnly(state.desired) ? 'No Frame/ Print Only' : 'Natural Wood') || inputFor(row, 'No Frame/ Print Only');
          if (desired) state.desired = desired.value;
        }
        if (!desired) { state.pending = true; guard(); return false; }
        if (!desired.checked) {
          var actionKey = key + ':' + normalise(desired.value) + ':' + state.generation;
          if (state.actionKey !== actionKey) { state.actionKey = actionKey; state.actionCount = 0; }
          if (state.actionCount < 3 && Date.now() - state.lastAction >= 200) {
            state.actionCount++;
            choose(desired);
          }
          state.pending = true;
          guard();
          return false;
        }
        if (allowed(desired.value)) state.lastFinish = desired.value;
        state.pending = Date.now() - state.lastAction < 500;
        if (!state.pending) { state.defaultVariant = null; state.expectedSize = null; }
        guard();
        return !state.pending;
      } finally { state.refreshing = false; }
    }
    /* Prices on every size card use that size's actual conditional VO group.
       Disabled groups are read here, never activated or counted as selections. */
    function updateSizePrices() {
      var control = widget();
      var reference = state.variant || currentVariant() || initialVariant;
      if (!control || !reference) return;
      var finish = state.desired == null ? 'No Frame/ Print Only' : state.desired;
      control.querySelectorAll('input.bcpo-front-button-option').forEach(function (input) {
        var label = labelFor(input);
        if (!label) return;
        var variant = product.variants.find(function (candidate) {
          return candidate.options.every(function (value, index) {
            return normalise(value) === normalise(index === sizeIndex ? input.value : reference.options[index]);
          });
        });
        var amount = variant ? variant.price : null;
        if (variant && !printOnly(finish)) {
          var expected = 'properties[frameoptions-' + sizeKey(input.value) + ']';
          var frameInput = null;
          rows().some(function (row) {
            frameInput = inputs(row).find(function (candidate) {
              return normalise(candidate.name) === expected && normalise(candidate.value) === normalise(finish);
            });
            return !!frameInput;
          });
          var extra = frameInput ? parseFloat(frameInput.getAttribute('data-price')) : NaN;
          amount = isFinite(extra) && extra >= 0 ? variant.price + Math.round(extra * 100) : null;
        }
        var price = label.querySelector('.ecas-cro-size-price');
        if (amount == null) { if (price) price.remove(); return; }
        if (!price) { price = document.createElement('span'); price.className = 'ecas-cro-size-price'; label.appendChild(price); }
        text(price, money(amount));
      });
    }
    function afterSync() {
      if (!state.active) return;
      guard();
      var variant = state.variant;
      var row = state.row;
      if (!variant || !row) return;
      var actual = selected(row);
      var framedInput = inputFor(row, state.lastFinish) || inputFor(row, 'Natural Wood');
      var framedAmount = framedInput ? Math.round(Number(framedInput.getAttribute('data-price')) * 100) : null;
      text(printButton.querySelector('strong'), money(variant.price));
      text(framedButton.querySelector('strong'), framedAmount == null ? '' : money(variant.price + framedAmount));
      if (framedButton.disabled !== !framedInput) framedButton.disabled = !framedInput;
      attr(printButton, 'aria-pressed', !!actual && printOnly(actual.value));
      attr(framedButton, 'aria-pressed', !!actual && !printOnly(actual.value));
      updateSizePrices();
      if (state.pending || !actual) return;
      var description = dimension(variant.options[sizeIndex].split('/')[0]) + ' · ' +
        (printOnly(actual.value) ? 'Print only. Frame not included.' : actual.value + ' frame included.');
      text(summary, description);
      text(stickyVariant, description);
      text(live, money(variant.price + surchargeCents()) + '. ' + description);
      attr(wrapper, 'data-ecas-selected-configuration', variant.id + ' / ' + actual.value);
      [mainPrice, stickyPrice].forEach(function (price) {
        var badge = price && price.querySelector('.price__badge');
        if (badge && variant.compare_at_price > variant.price) text(badge, 'Save ' + money(variant.compare_at_price - variant.price));
        if (price) {
          var code = price.querySelector('.ecas-cro-currency');
          if (!code) { code = document.createElement('span'); code.className = 'ecas-cro-currency'; price.appendChild(code); }
          text(code, currency);
        }
      });
    }
    /* If VO cannot settle, restore its ordinary controls and price bridge. */
    function fallback() {
      state.active = false;
      api.active = false;
      clearInterval(poll);
      poll = null;
      observer.disconnect();
      ['pointerdown', 'keydown', 'click', 'change', 'input'].forEach(function (type) { document.removeEventListener(type, onGesture, true); });
      document.removeEventListener('submit', blockPending, true);
      document.removeEventListener('click', blockPending, true);
      [addButton, stickyButton].forEach(function (button) {
        if (!button || !button.hasAttribute('data-ecas-pending')) return;
        button.removeAttribute('data-ecas-pending');
        button.disabled = !!(state.variant && !state.variant.available);
      });
      [mainPrice, stickyPrice].forEach(function (price) {
        if (!price) return;
        price.classList.remove('ecas-price-pending');
        price.removeAttribute('aria-busy');
        price.querySelectorAll('.ecas-cro-currency').forEach(function (code) { code.remove(); });
      });
      [summary, live, format, style].forEach(function (element) { element.remove(); });
      wrapper.classList.remove('ecas-route66-pdp');
      wrapper.removeAttribute('data-ecas-configuration-state');
      wrapper.removeAttribute('data-ecas-selected-configuration');
      document.body.classList.remove('ecas-route66-pdp-page');
      var control = widget();
      if (control) {
        control.classList.remove('ecas-cro-size-grid');
        control.removeAttribute('role');
        control.removeAttribute('aria-labelledby');
        control.querySelectorAll('input.bcpo-front-button-option').forEach(function (input) {
          var label = labelFor(input);
          if (label) { label.textContent = input.value; label.removeAttribute('data-ecas-label'); }
        });
      }
      if (state.root) {
        state.root.querySelectorAll('.ecas-cro-size-heading, .ecas-cro-finish-heading, .ecas-cro-finish-name').forEach(function (el) { el.remove(); });
        rows().forEach(function (row) {
          row.classList.remove('ecas-cro-finish-row');
          inputs(row).forEach(function (input) {
            var cell = input.closest('.bcpo-image-parent');
            if (cell) { cell.classList.remove('ecas-cro-print-option', 'ecas-cro-extra'); cell.removeAttribute('aria-hidden'); }
            if (printOnly(input.value)) input.removeAttribute('tabindex');
          });
        });
        initFrameSwatches(state.root);
      }
      if (control) addArrows(control, 'ecas-size-wrap');
      scheduleSync();
    }
    guard();
    return api;
  }


  /* Initial product configuration only. Use the product's existing controls so
     VO keeps ownership of its conditional options and frame charges. */
  function initPreferredConfiguration() {
    if (!SITEWIDE_DEFAULT_ENABLED && ENTRY_PARAMS.get('ecas_defaults_preview') !== '1') return;
    if (ENTRY_PARAMS.has('variant') || ENTRY_PARAMS.has('option_values')) return;

    var wrapper = document.querySelector('[data-product-form-wrapper][data-update-url="true"]');
    var dataEl = wrapper && wrapper.querySelector('[data-product-json]');
    var idInput = wrapper && wrapper.querySelector('form[data-product-form] input[name="id"]');
    if (!dataEl || !idInput) return;
    var product;
    try { product = JSON.parse(dataEl.textContent); } catch (err) { return; }
    if (!Array.isArray(product.variants) || !Array.isArray(product.options)) return;

    function normalise(value) {
      return String(value == null ? '' : value).toLowerCase()
        .replace(/\s+/g, '').replace(/×/g, 'x').replace(/["″]/g, '');
    }
    var sizeIndex = product.options.findIndex(function (option) {
      return normalise(typeof option === 'string' ? option : option.name) === 'size';
    });
    var current = product.variants.find(function (variant) {
      return String(variant.id) === String(idInput.value);
    });
    if (sizeIndex < 0 || !current) return;
    var preferred = product.variants.find(function (variant) {
      return variant.available && variant.options.every(function (value, index) {
        return index === sizeIndex
          ? /^(?:11x14|14x11)(?:in(?:ch(?:es)?)?)?(?:\/|$)/.test(normalise(value))
          : normalise(value) === normalise(current.options[index]);
      });
    });
    /* Preserve the normal selection if that combination is absent/sold out. */
    if (!preferred) return;

    var attempts = 0;
    var sizeRequested = false;
    var frameRequested = false;
    var frameRequestedAt = 0;
    var timer;
    var stopped = false;
    var choosing = false;
    function stop() {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('pointerdown', onInteraction, true);
      document.removeEventListener('keydown', onInteraction, true);
      document.removeEventListener('click', onInteraction, true);
      document.removeEventListener('change', onInteraction, true);
      document.removeEventListener('input', onInteraction, true);
      document.removeEventListener('submit', onSubmit, true);
    }
    function onInteraction(event) {
      var target = event.target;
      if (!choosing && event.isTrusted && target && target.closest && target.closest(
        APP_ROOTS + ', [data-option-index], [data-add-to-cart], [data-sticky-add]'
      )) stop();
    }
    function choose(input) {
      var label = input.parentElement && Array.prototype.find.call(
        input.parentElement.querySelectorAll('label'),
        function (candidate) { return candidate.htmlFor === input.id; }
      );
      var scrollX = window.scrollX;
      var scrollY = window.scrollY;
      var focused = document.activeElement;
      choosing = true;
      try {
        (label || input).click();
        if (focused && focused !== document.body && focused.isConnected && focused.focus) {
          focused.focus({ preventScroll: true });
        } else if (document.activeElement && document.activeElement !== focused && document.activeElement.blur) {
          document.activeElement.blur();
        }
        window.scrollTo({ left: scrollX, top: scrollY, behavior: 'instant' });
      } finally { choosing = false; }
    }
    function onSubmit(event) {
      if (event.target && event.target.matches('form[data-product-form]')) stop();
    }
    function attempt() {
      if (stopped) return;
      if (++attempts > MAX_DEFAULT_ATTEMPTS) { stop(); return; }
      var sizeWidget = document.getElementById(OPTION_WIDGET_PREFIX + sizeIndex);
      var sizeInput = sizeWidget && Array.prototype.find.call(
        sizeWidget.querySelectorAll('input.bcpo-front-button-option[type="radio"]'),
        function (input) {
          return !input.disabled && normalise(input.value) === normalise(preferred.options[sizeIndex]);
        }
      );
      if (!sizeInput) return;
      if (!sizeInput.checked) {
        if (!sizeRequested) {
          sizeRequested = true;
          choose(sizeInput);
          scheduleSync();
        }
        return;
      }
      /* A size click can precede the app's conditional-row update. Match the
         enabled, visible 11x14/14x11 group, never a stale 5x7 frame. */
      var frame = Array.prototype.find.call(
        document.querySelectorAll('#bcpo input.bcpo-front-image-option[type="radio"]'),
        function (input) {
          return !input.disabled && isOnScreen(input) &&
            /^properties\[frameoptions-(?:11x14|14x11)\]$/.test(normalise(input.name)) &&
            normalise(input.value) === 'naturalwood';
        }
      );
      if (!frame) return;
      if (frame.checked && (!frameRequested || Date.now() - frameRequestedAt >= 500)) {
        stop();
        scheduleSync();
      } else if (!frameRequested) {
        frameRequested = true;
        frameRequestedAt = Date.now();
        choose(frame);
        scheduleSync();
      }
    }
    /* Actual gestures, including assistive-technology clicks, cancel pending
       work. Ignore synchronous events generated by our own label activation. */
    document.addEventListener('pointerdown', onInteraction, true);
    document.addEventListener('keydown', onInteraction, true);
    document.addEventListener('click', onInteraction, true);
    document.addEventListener('change', onInteraction, true);
    document.addEventListener('input', onInteraction, true);
    document.addEventListener('submit', onSubmit, true);
    timer = setInterval(attempt, POLL_MS);
    attempt();
  }

  /* Presentation only: confirm the existing VO/native price bridge before
     replacing surcharge language with complete prices on legacy frame PDPs. */
  function initLegacyFramePresentation(root) {
    var wrappers = document.querySelectorAll('[data-product-form-wrapper]');
    if (wrappers.length !== 1) return null;
    var wrapper = wrappers[0];
    var data = wrapper.querySelector('[data-product-json]');
    var form = wrapper.querySelector('form[data-product-form]');
    var idInput = form && form.querySelector('input[name="id"]');
    var product;
    try { product = JSON.parse(data.textContent); } catch (err) { return null; }
    if (!form || !idInput || !wrapper.contains(root) ||
        String(product.id) === '8635085750410' ||
        !Array.isArray(product.options) || product.options.length !== 1 ||
        !Array.isArray(product.variants)) return null;
    var optionName = typeof product.options[0] === 'string'
      ? product.options[0] : product.options[0].name;
    if (normalise(optionName) !== 'size') return null;

    var price = wrapper.querySelector('[data-price]');
    var stickyPrice = document.querySelector('[data-sticky-price]');
    var stickyVariant = document.querySelector('[data-sticky-variant]');
    var summary = null;
    var supported = false;
    var lastSize = '';
    var revealKey = '';
    var revealTimer = null;
    var moneyProbe = document.createElement('span');

    function normalise(value) {
      return String(value == null ? '' : value).toLowerCase()
        .replace(/\s+/g, '').replace(/×/g, 'x');
    }
    function text(el, value) {
      if (el && el.textContent !== value) el.textContent = value;
    }
    function dimensions(value, frame) {
      var source = normalise(value);
      var match = frame
        ? source.match(/^properties\[frameoptions-(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)\]$/)
        : source.split('/')[0].match(/^(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)(?:in(?:ch(?:es)?)?|["″])$/);
      if (!match) return null;
      var a = Number(match[1]);
      var b = Number(match[2]);
      if (!(a > 0 && b > 0)) return null;
      return { key: Math.min(a, b) + 'x' + Math.max(a, b), label: a + ' × ' + b + ' in' };
    }
    function charge(input) {
      var raw = input.getAttribute('data-price');
      if (raw == null || !/^\s*\d+(?:\.\d+)?\s*$/.test(raw)) return null;
      var amount = Number(raw);
      return isFinite(amount) && amount >= 0 ? Math.round(amount * 100) : null;
    }
    function labelFor(input) {
      if (!input.id || !input.parentElement) return null;
      return Array.prototype.find.call(input.parentElement.querySelectorAll('label.bcpo-front-image-label'), function (label) {
        return label.htmlFor === input.id;
      }) || null;
    }
    function currentVariant() {
      return product.variants.find(function (variant) {
        return String(variant.id) === String(idInput.value);
      }) || null;
    }
    /* Same money placeholders as theme.js. Detached markup decoding lets
       captions and verification match the store's own currency formatting. */
    function money(cents) {
      var format = window.theme && window.theme.moneyFormat;
      if (typeof format !== 'string' || !isFinite(cents)) return null;
      var valid = true;
      var found = false;
      function delimiters(precision, thousands, decimal) {
        var parts = (Math.round(cents) / 100).toFixed(precision).split('.');
        var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts[1] ? whole + decimal + parts[1] : whole;
      }
      var html = format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (token, name) {
        found = true;
        switch (name) {
          case 'amount': return delimiters(2, ',', '.');
          case 'amount_no_decimals': return delimiters(0, ',', '.');
          case 'amount_with_comma_separator': return delimiters(2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return delimiters(0, '.', ',');
          case 'amount_with_apostrophe_separator': return delimiters(2, "'", '.');
          case 'amount_with_space_separator': return delimiters(2, ' ', ',');
          case 'amount_no_decimals_with_space_separator': return delimiters(0, ' ', ',');
          default: valid = false; return token;
        }
      });
      if (!valid || !found) return null;
      moneyProbe.innerHTML = html;
      return moneyProbe.textContent.trim();
    }
    function displayedMatches(container, amount) {
      var value = container && container.querySelector('.price__sale');
      var expected = money(amount);
      return !!(value && expected && normalise(value.textContent) === normalise(expected));
    }
    function visibleControl(control) {
      if (control.disabled || control.type === 'hidden') return false;
      if (control.tagName === 'OPTION') {
        var select = control.closest('select');
        return !!(select && !select.disabled && select.offsetParent);
      }
      return isOnScreen(control);
    }
    function readState() {
      if (!root.isConnected || document.getElementById('bcpo') !== root) return null;
      var widget = document.getElementById(OPTION_WIDGET_PREFIX + '0');
      if (!widget || !wrapper.contains(widget)) return null;
      var sizes = Array.prototype.filter.call(widget.querySelectorAll('input.bcpo-front-button-option[type="radio"]'), function (input) {
        return input.checked && !input.disabled;
      });
      if (sizes.length !== 1) return null;
      var variant = currentVariant();
      if (!variant || !Array.isArray(variant.options) || variant.options.length !== 1 ||
          normalise(variant.options[0]) !== normalise(sizes[0].value) ||
          !isFinite(variant.price) || Number(variant.price) < 0) return null;
      var size = dimensions(sizes[0].value, false);
      if (!size) return null;
      var groups = [];
      root.querySelectorAll('.bcpo-images').forEach(function (row) {
        var inputs = Array.prototype.filter.call(row.querySelectorAll('input.bcpo-front-image-option[type="radio"]'), function (input) {
          return !input.disabled && isOnScreen(input);
        });
        if (inputs.length) groups.push({ row: row, inputs: inputs });
      });
      if (groups.length !== 1) return null;
      var group = groups[0];
      var name = group.inputs[0].name;
      var frameSize = dimensions(name, true);
      if (!frameSize || frameSize.key !== size.key) return null;
      var choices = [];
      for (var i = 0; i < group.inputs.length; i++) {
        var input = group.inputs[i];
        var extra = charge(input);
        var label = labelFor(input);
        if (input.name !== name || input.form !== form || extra == null || !label || !input.value) return null;
        choices.push({ input: input, label: label, charge: extra });
      }
      var chosen = choices.filter(function (choice) { return choice.input.checked; });
      if (chosen.length > 1) return null;

      /* Unsupported active virtual fields retain the original presentation.
         Disabled size-specific rows and the app's hidden duplicate controls
         are deliberately ignored. */
      var other = false;
      root.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["], [data-price]').forEach(function (control) {
        if (other || group.inputs.indexOf(control) !== -1 || !visibleControl(control)) return;
        var sameFrame = control.name === name && control.tagName === 'SELECT' && !control.offsetParent;
        if (!sameFrame) other = true;
      });
      if (other) return null;
      var extra = chosen.length ? chosen[0].charge : 0;
      if (surchargeCents() !== extra) return null;
      /* A checked radio alone does not prove VO accepted its paid option.
         Confirm the app's own computed add-on before claiming a full price. */
      var appAmounts = root.querySelectorAll('.bcpo-price-addons .bcpo-addons-price');
      var appExpected = money(extra);
      if (appAmounts.length !== 1 || !appExpected ||
          normalise(appAmounts[0].textContent) !== normalise(appExpected)) return null;
      var total = Number(variant.price) + extra;
      if (!displayedMatches(price, total) || (stickyPrice && !displayedMatches(stickyPrice, total))) return null;
      return { variant: variant, size: size, widget: widget, nativeInput: sizes[0], choices: choices, chosen: chosen[0] || null };
    }
    function resetCaptions() {
      root.querySelectorAll('.ecas-frame-total').forEach(function (caption) {
        var cell = caption.closest('.bcpo-image-parent');
        var radio = cell && cell.querySelector('input[type="radio"]');
        var amount = radio ? parseFloat(radio.getAttribute('data-price') || '') : 0;
        caption.classList.remove('ecas-frame-total');
        if (amount > 0) text(caption, '+$' + amount.toFixed(2));
        else caption.remove();
      });
    }
    function invalidate() {
      wrapper.classList.remove('ecas-price-synced', 'ecas-choice-needed');
      if (summary) summary.hidden = true;
      var variant = currentVariant();
      if (supported && stickyVariant && variant) text(stickyVariant, variant.title || variant.options.join(' / '));
      resetCaptions();
    }
    function renderChoices(state) {
      state.choices.forEach(function (choice) {
        var cell = choice.input.parentElement;
        var caption = cell.querySelector('.ecas-frame-name');
        if (!caption || caption.tagName !== 'LABEL' || caption.htmlFor !== choice.input.id) {
          cell.querySelectorAll('.ecas-frame-name, .ecas-frame-price').forEach(function (node) { node.remove(); });
          caption = document.createElement('label');
          caption.className = 'ecas-frame-name';
          caption.htmlFor = choice.input.id;
          caption.appendChild(document.createTextNode(choice.input.value));
          caption.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();
            if (!choice.input.disabled) choice.label.click();
          });
          cell.appendChild(caption);
        }
        var amount = caption.querySelector('.ecas-frame-price');
        if (!amount) {
          amount = document.createElement('span');
          amount.className = 'ecas-frame-price';
          caption.appendChild(amount);
        }
        amount.classList.add('ecas-frame-total');
        text(amount, money(Number(state.variant.price) + choice.charge) + ' total');
      });
    }
    /* The app and arrow wrapper settle after the first price pass. Reveal a
       newly selected size once, without snapping back after customer panning. */
    function revealSize(state) {
      var key = String(state.variant.id);
      if (key === lastSize || key === revealKey) return;
      clearTimeout(revealTimer);
      revealKey = key;
      var attempts = 0;
      var previous = '';
      function measure() {
        if (revealKey !== key) return;
        var current = currentVariant();
        if (!current || String(current.id) !== key || !state.widget.isConnected ||
            !state.nativeInput.isConnected || !state.nativeInput.checked ||
            document.getElementById(OPTION_WIDGET_PREFIX + '0') !== state.widget) {
          revealKey = '';
          return;
        }
        var label = Array.prototype.find.call(state.nativeInput.parentElement.querySelectorAll('label'), function (candidate) {
          return candidate.htmlFor === state.nativeInput.id;
        });
        var rowRect = state.widget.getBoundingClientRect();
        var labelRect = label && label.getBoundingClientRect();
        var ready = state.widget.closest('.ecas-size-wrap') && labelRect &&
          state.widget.clientWidth > 0 && rowRect.width > 0 && labelRect.width > 0 && labelRect.height > 0;
        var geometry = ready ? [state.widget.clientWidth, state.widget.scrollWidth,
          rowRect.left, rowRect.right, labelRect.left, labelRect.right].map(function (value) {
          return Math.round(value * 10);
        }).join(':') : '';
        if (!ready || geometry !== previous) {
          previous = geometry;
          if (++attempts < 6) revealTimer = setTimeout(measure, 125);
          else revealKey = '';
          return;
        }
        /* Consume this key only after the controls have a stable real layout. */
        lastSize = key;
        revealKey = '';
        if (state.widget.scrollWidth <= state.widget.clientWidth + SCROLL_EPSILON) return;
        var inset = 48;
        var next = state.widget.scrollLeft;
        if (labelRect.left < rowRect.left + inset) next += labelRect.left - rowRect.left - inset;
        else if (labelRect.right > rowRect.right - inset) next += labelRect.right - rowRect.right + inset;
        else return;
        next = Math.max(0, Math.min(state.widget.scrollWidth - state.widget.clientWidth, next));
        state.widget.scrollTo({ left: next, behavior: 'instant' });
      }
      revealTimer = setTimeout(measure, 300);
    }
    function cancelReveal(event) {
      if (!revealKey || !event.isTrusted || !event.target || !event.target.closest ||
          !event.target.closest('.ecas-size-wrap')) return;
      if (event.type === 'wheel' && !event.deltaX && !event.shiftKey) return;
      if (event.type === 'keydown' && !event.target.closest('.ecas-arr')) return;
      lastSize = revealKey;
      revealKey = '';
      clearTimeout(revealTimer);
    }
    root.addEventListener('pointerdown', cancelReveal, true);
    root.addEventListener('wheel', cancelReveal, { capture: true, passive: true });
    root.addEventListener('keydown', cancelReveal, true);
    function afterSync() {
      var state = readState();
      if (!state) { invalidate(); return; }
      supported = true;
      wrapper.classList.add('ecas-pdp-cleanup');
      if (!summary || !summary.isConnected) {
        summary = document.createElement('p');
        summary.className = 'ecas-selection-summary';
        summary.setAttribute('aria-live', 'polite');
        price.insertAdjacentElement('afterend', summary);
      }
      renderChoices(state);
      var description = 'Choose print only or a frame';
      if (state.chosen) {
        var frame = state.chosen.input.value;
        var printOnly = /no\s*frame|print\s*only/i.test(frame);
        description = state.size.label + ' · ' + (printOnly ? 'Print only · No frame' : frame + ' frame included');
        if (stickyVariant) text(stickyVariant, description);
      }
      text(summary, description);
      summary.hidden = false;
      wrapper.classList.toggle('ecas-price-synced', !!state.chosen);
      wrapper.classList.toggle('ecas-choice-needed', !state.chosen);
      revealSize(state);
    }
    return { afterSync: afterSync, invalidate: invalidate, isSupported: function () { return supported; } };
  }

  /* ------------------------------------------------------------------
     Frame swatches: heading, per-swatch name and surcharge, arrows
     ------------------------------------------------------------------ */
  function initFrameSwatches(root) {
    if (route66Pdp && route66Pdp.active) { route66Pdp.refresh(); return; }
    if (!legacyFramePdp) legacyFramePdp = initLegacyFramePresentation(root);
    var busy = false;
    var headerPlaced = false;

    var headerContainer = document.createElement('div');
    headerContainer.id = 'ecas-header-container';

    var heading = document.createElement('div');
    heading.className = 'ecas-frame-header';
    headerContainer.appendChild(heading);

    var badge = document.createElement('div');
    badge.className = 'ecas-ready-hang';
    badge.hidden = true;
    badge.textContent = '✓ Ready to Hang';
    headerContainer.appendChild(badge);

    /* The app renders one row per size and disables the rows that do not
       apply, so the live row is the last one holding an enabled radio. */
    function activeRow() {
      var found = null;
      root.querySelectorAll('.bcpo-images').forEach(function (row) {
        var radio = row.querySelector('input[type="radio"]');
        if (radio && !radio.disabled) found = row;
      });
      return found;
    }

    function initArrows() {
      root.querySelectorAll('.bcpo-images').forEach(function (row) {
        var radio = row.querySelector('input[type="radio"]');
        if (radio && radio.disabled) return;
        var update = addArrows(row, 'ecas-wrap');
        if (update) update();
      });
    }

    function render() {
      if (busy) return;
      busy = true;

      root.querySelectorAll('.ecas-frame-name, .ecas-frame-price').forEach(function (el) {
        el.remove();
      });
      root.querySelectorAll('.bcpo-images > .ecas-arr').forEach(function (el) {
        el.remove();
      });

      var row = activeRow();
      if (row && !headerPlaced) {
        var anchor = row.closest('.ecas-wrap') || row;
        anchor.parentNode.insertBefore(headerContainer, anchor);
        headerPlaced = true;
      }

      var selectedName = '';
      if (row) {
        row.querySelectorAll('div.bcpo-image-parent').forEach(function (cell) {
          var radio = cell.querySelector('input[type="radio"]');
          if (radio && radio.checked) selectedName = radio.getAttribute('value') || '';
        });
      }

      heading.textContent = 'Frame:';
      if (selectedName) {
        var name = document.createElement('strong');
        name.textContent = selectedName;
        heading.appendChild(document.createTextNode(' '));
        heading.appendChild(name);
      }
      /* Only a real frame is ready to hang; the print-only options are not. */
      badge.hidden = !selectedName || /no frame|print only/i.test(selectedName);

      root.querySelectorAll('.bcpo-images div.bcpo-image-parent').forEach(function (cell) {
        var radio = cell.querySelector('input[type="radio"]');
        if (!radio) return;

        var name = document.createElement('span');
        name.className = 'ecas-frame-name';
        name.textContent = radio.getAttribute('value') || '';
        cell.appendChild(name);

        var surcharge = parseFloat(radio.getAttribute('data-price') || '');
        if (surcharge > 0) {
          var price = document.createElement('span');
          price.className = 'ecas-frame-price';
          price.textContent = '+$' + surcharge.toFixed(2);
          cell.appendChild(price);
        }
      });

      /* The headline price is read from the same state just drawn here, so the
         surcharge under the swatch and the price above it never disagree. */
      syncPrice();

      /* The app finishes its own repaint after the click that triggered this,
         so measuring for arrows has to wait for it to settle. */
      setTimeout(function () {
        initArrows();
        busy = false;
      }, 250);
    }

    render();

    root.addEventListener('click', function (event) {
      if (event.target.closest('.bcpo-image-parent')) setTimeout(render, 350);
    });

    root.querySelectorAll('select').forEach(function (select) {
      select.addEventListener('change', function () {
        headerPlaced = false;
        setTimeout(render, 500);
      });
    });

    /* Rebuild when the app replaces its markup — but ignore the mutations this
       script itself causes, or render() would retrigger itself forever. */
    var observer = new MutationObserver(function (mutations) {
      if (busy) return;
      var ours = mutations.every(function (mutation) {
        var target = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;
        if (target && target.closest('.ecas-frame-name, .ecas-frame-price, .ecas-arr, #ecas-header-container')) return true;
        if (mutation.type === 'attributes') return false;
        var touched = [].concat(
          [].slice.call(mutation.addedNodes),
          [].slice.call(mutation.removedNodes)
        );
        return touched.every(function (node) {
          return node.nodeType === 1 && String(node.className || '').indexOf('ecas') !== -1;
        });
      });
      if (ours) return;

      headerPlaced = false;
      setTimeout(render, 300);
    });

    root.querySelectorAll('.bcpo-images').forEach(function (row) {
      var options = { childList: true, subtree: true };
      if (legacyFramePdp) {
        options.attributes = true;
        options.attributeFilter = ['disabled', 'checked', 'style', 'data-price'];
      }
      observer.observe(row.parentNode, options);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    route66Pdp = initRoute66Pdp();
    if (!route66Pdp) initPreferredConfiguration();

    /* Capture phase: the app stops propagation on its own controls, and so do
       the scroll arrows added above. */
    ['click', 'change'].forEach(function (type) {
      document.addEventListener(type, function (event) {
        var target = event.target;
        if (target && target.closest && target.closest(APP_ROOTS)) {
          if (legacyFramePdp && !target.closest('.ecas-arr')) legacyFramePdp.invalidate();
          scheduleSync();
        }
      }, true);
    });

    whenReady(
      function () {
        var root = document.getElementById('bcpo');
        return root && root.querySelector('.bcpo-images') ? root : null;
      },
      initFrameSwatches
    );

    /* The size row is a separate widget with its own container. */
    whenReady(
      function () {
        var row = document.querySelector('#' + OPTION_WIDGET_PREFIX + '0');
        return row && row.children.length ? row : null;
      },
      function (row) {
        if (!route66Pdp || !route66Pdp.active) addArrows(row, 'ecas-size-wrap');
        /* The app applies its own default size once it has rendered; the price
           has to start from that, not from the variant Liquid happened to
           render. A product with no frame swatches gets its only sync here. */
        scheduleSync();
      }
    );
  });
})();




